import React from 'react';
import PricingTable from '../common/pricing-table';
import InfoIcon from '@mui/icons-material/Info';
import Typography from '@mui/material/Typography';
import blue from '@mui/material/colors/blue';
import md5 from 'md5';
import {
  PLUS,
  PREMIUM,
  PREMIUM_CAPTURED_PRICE,
  UDLEJ100,
  PREMIUM200,
  UDLEJ10P,
  PREMIUM20P
} from '../../lib/paylike/prices';
import { useQuery } from '../hooks/useQuery';

const tiers = [
  {
    info: 'Deling på relevante Facebooksider og Facebookgrupper',
    standard: '1 gang',
    plus: '2 gange',
    premium: '2 gange'
  },
  {
    info: 'Visning i søgeresultater',
    standard: 'Kronologisk efter oprettelsestidspunkt',
    plus: 'Øverst kronologisk efter Premium annoncer',
    premium: 'Øverst'
  },
  {
    info: 'Brugeres adgang til udlejers kontaktinfo',
    standard: 'Kun betalende brugere',
    plus: 'Alle registrerede brugere',
    premium: 'Alle besøgende'
  },
  {
    info: 'Mulighed for at fravælge bestemte kontaktformer',
    standard: 'Kun beskedsystem',
    plus: 'Vælg selv kontaktformer (beskedsystem, emails, telefon)',
    premium: 'Vælg selv kontaktformer (beskedsystem, emails, telefon)'
  },
  {
    info: 'Boligbesked e-mail',
    standard: 'Sendes ved oprettelse',
    plus: 'Sendes ved oprettelse og efter 7 dage',
    premium: 'Sendes ved oprettelse og gensending efter 7 dage'
  },
  {
    info: 'Fremhævet visning i søgeresultater',
    standard: false,
    plus: false,
    premium: true
  },
  {
    info: 'Boost af annonce på Facebook *',
    standard: false,
    plus: false,
    premium: true
  },
  {
    info: 'Pris pr annonceret lejemål inkl. moms **',
    standard: 'Gratis',
    plus: `${PLUS} kr.`,
    premium: `${PREMIUM} kr.`
  }
];

function hashSecret(secret) {
  return md5(JSON.stringify({ secret }));
}

function getActions(listingType, captured, discounts) {
  const all = [];
  const plus = [];
  const premium = [];

  for (let index = 0; index < discounts.length; index++) {
    const d = discounts[index];

    const discount = {
      code: d.hash,
      flat: d.flat,
      percentage: d.percentage
    };

    switch (d.listingType) {
      case 'PLUS':
        plus.push(discount);
        break;
      case 'PREMIUM':
        premium.push(discount);
        break;
      default:
        all.push(discount);
        break;
    }
  }

  switch (listingType) {
    case 'STANDARD':
      return [
        { value: 'STANDARD', amount: 0, label: 'Valgt', discounts: [] },
        {
          value: 'PLUS',
          amount: PLUS,
          label: 'Opgrader',
          discounts: [
            { code: hashSecret('udlej100'), flat: UDLEJ100 },
            { code: hashSecret('udlej10p'), percentage: UDLEJ10P }
          ].concat(all, plus)
        },
        {
          value: 'PREMIUM',
          amount: PREMIUM,
          label: 'Opgrader',
          discounts: [
            { code: hashSecret('udlej100'), flat: UDLEJ100 },
            { code: hashSecret('udlej10p'), percentage: UDLEJ10P },
            { code: hashSecret('premium200'), flat: PREMIUM200 },
            { code: hashSecret('premium20p'), percentage: PREMIUM20P }
          ].concat(all, premium)
        }
      ];
    case 'PLUS':
      return [
        null,
        {
          value: 'PLUS',
          amount: PLUS,
          label: captured ? 'Betaling bekræftet' : 'Valgt',
          discounts: []
        },
        {
          value: 'PREMIUM',
          amount: captured ? PREMIUM_CAPTURED_PRICE : PREMIUM,
          label: captured
            ? `Opgrader for ${PREMIUM_CAPTURED_PRICE} kr.`
            : 'Opgrader',
          discounts: [
            { code: hashSecret('udlej100'), flat: UDLEJ100 },
            { code: hashSecret('udlej10p'), percentage: UDLEJ10P },
            { code: hashSecret('premium200'), flat: PREMIUM200 },
            { code: hashSecret('premium20p'), percentage: PREMIUM20P }
          ].concat(all, premium)
        }
      ];

    default:
      return null;
  }
}

const DISCOUNTS = `
query GetDiscounts($activeOnly: Boolean){
    discounts(activeOnly: $activeOnly) {
        hash
        flat
        percentage
        user
        start
        listingType
        end
        status
    }
}
`;

const PaymentTable = (props) => {
  const { onPayment, dwelling } = props;
  const { listingType, payment } = dwelling;

  const { data } = useQuery(DISCOUNTS, {
    variables: { activeOnly: true }
  });

  return (
    <div>
      <PricingTable
        mutationOptions={props.mutationOptions}
        id={dwelling.uuid || dwelling.id}
        title={props.title}
        columns={[
          { label: 'Info', name: 'info', hide: true },
          { label: 'Standard', name: 'standard' },
          {
            label: 'Plus',
            name: 'plus',
            feat: 'Anbefalet for flest mulige henvendelser fra boligsøgende'
          },
          { label: 'Premium', name: 'premium' }
        ]}
        info={
          <>
            {['PLUS', 'PREMIUM'].includes(listingType) && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0em 1em',
                  borderRadius: '4px',
                  backgroundColor: blue[100],
                  marginBottom: '.8em'
                }}
              >
                <InfoIcon style={{ marginRight: '.4em', color: blue[700] }} />
                <p>
                  Du har tilkøbt <strong>{listingType}</strong>
                  {payment && payment.captured
                    ? ''
                    : ', vi trækker først pengene når din annonce er godkendt'}
                </p>
              </div>
            )}
            <Typography variant="caption" component="p" color="textSecondary">
              * Sponsoreret annonce til målrettet segment på Facebook
            </Typography>
            <Typography variant="caption" component="p" color="textSecondary">
              ** Prisen er en engangsbetaling pr. annonce, og serviceydelserne
              gælder i hele annonceringsperioden
            </Typography>
          </>
        }
        listingType={listingType}
        onPayment={onPayment}
        rows={tiers}
        actions={getActions(
          listingType,
          payment && payment.captured,
          data?.discounts || []
        )}
      />
    </div>
  );
};

export default PaymentTable;